import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { configuration } from "src/configuration";
import {
  EditOwnershipDto,
  FetchError,
  GetTeamTaskItemsDto,
  GetUserTaskItemsDto,
  optimisticallyUpdateQueryData,
  queryKeys,
  TaskItemDetailDto,
  TaskItemOwnerDto,
  useAccessToken,
  UserDto,
  useTaskItemMutation,
} from "src/http";

type Variables = {
  taskItemGuid: string;
  owners: UserDto[];
  primaryOwner: UserDto | null;
  teamSlug?: string;
};

const useEditTaskItemOwnersRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemOwnersRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditOwnershipDto = {
      ownerUserIds: variables.owners.map((x) => x.userId),
      primaryOwnerUserId: variables.primaryOwner?.userId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/owners`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemOwnersRequest };
};

const useEditTaskItemOwners = () => {
  const { editTaskItemOwnersRequest } = useEditTaskItemOwnersRequest();
  const queryClient = useQueryClient();

  return useTaskItemMutation({
    mutationFn: editTaskItemOwnersRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;
      let cachedUserData: { [userGuid: string]: GetUserTaskItemsDto | undefined } = {};
      let cachedTeamData: GetTeamTaskItemsDto | undefined;

      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        (cachedData) => ({
          ...cachedData,
          owners: variables.owners.map((user) => ({
            user: user,
            isFocused: cachedData.owners.find((o) => o.user.userId === user.userId)?.isFocused || false,
          })),
          primaryOwner: variables.primaryOwner,
        })
      );

      let oldOwners = cachedDetailData?.owners.map((x) => x.user) || [];
      let ownersToRemove = _.differenceBy(oldOwners, variables.owners, (x) => x.userId);
      let ownersToUpdate = _.intersectionBy(oldOwners, variables.owners, (x) => x.userId);

      ownersToRemove.forEach(async (owner) => {
        cachedUserData[owner.userId] = await optimisticallyUpdateQueryData<GetUserTaskItemsDto>(
          queryClient,
          queryKeys.taskItems.getUserTaskItems(owner.userId),
          (cachedData) => ({
            taskItems: cachedData.taskItems.filter((taskItem) => taskItem.guid !== variables.taskItemGuid),
          })
        );
      });

      ownersToUpdate.forEach(async (owner) => {
        cachedUserData[owner.userId] = await optimisticallyUpdateQueryData<GetUserTaskItemsDto>(
          queryClient,
          queryKeys.taskItems.getUserTaskItems(owner.userId),
          (cachedData) => ({
            taskItems: cachedData.taskItems.map((taskItem) => {
              if (taskItem.guid === variables.taskItemGuid) {
                return {
                  ...taskItem,
                  owners: variables.owners.map(
                    (user): TaskItemOwnerDto => ({
                      user: user,
                      isFocused: false,
                    })
                  ),
                  primaryOwner: variables.primaryOwner,
                };
              } else {
                return taskItem;
              }
            }),
          })
        );
      });

      if (cachedDetailData?.team != null) {
        cachedTeamData = await optimisticallyUpdateQueryData<GetTeamTaskItemsDto>(
          queryClient,
          queryKeys.taskItems.getTeamTaskItems(cachedDetailData.team.slug),
          (cachedData) => ({
            taskItems: cachedData.taskItems.map((taskItem) => {
              if (taskItem.guid === variables.taskItemGuid) {
                return {
                  ...taskItem,
                  owners: variables.owners.map(
                    (user): TaskItemOwnerDto => ({
                      user: user,
                      isFocused: false,
                    })
                  ),
                  primaryOwner: variables.primaryOwner,
                };
              } else {
                return taskItem;
              }
            }),
          })
        );
      }

      return {
        cachedDetailData,
        cachedUserData,
        cachedTeamData,
      };
    },
  });
};

export { useEditTaskItemOwners };
